// frontend/src/contexts/AuthContext.tsx
import React, { createContext, useState, useContext, useEffect, ReactNode, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthService from '../services/auth.service';
import { User, AuthState } from '../types';
import { toast } from 'react-toastify';

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  register: (
    firstName: string, 
    lastName: string, 
    email: string, 
    password: string,
    planType?: string,
    planDuration?: string,
    currency?:string
  ) => Promise<{
    user: User;
    accessToken: string;
    payment?: {
      reference: string;
      authorizationUrl: string;
    }
  }>;
  logout: () => void;
  refreshUser: () => Promise<User>;
  setAuthState: (state: Partial<AuthState>) => void;
}

const defaultAuthContext: AuthContextType = {
  isAuthenticated: false,
  currentUser: null,
  loading: true,
  error: null,
  login: async () => {},
  register: async () => ({ 
    user: { id: '', firstName: '', lastName: '', email: '', role: '', isVerified: false, isPremium: false },
    accessToken: ''
  }),
  logout: () => {},
  refreshUser: async () => ({ id: '', firstName: '', lastName: '', email: '', role: '', isVerified: false, isPremium: false }),
  setAuthState: () => {}
};

const AuthContext = createContext<AuthContextType>(defaultAuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    currentUser: null,
    loading: true,
    error: null
  });
  const navigate = useNavigate();
  const location = useLocation();
  const redirectRef = useRef(false);

  // Initialize auth state (run once)
  useEffect(() => {
    const initAuth = async () => {
      try {
        const user = await AuthService.getCurrentUser();
        console.log('AuthProvider - getCurrentUser:', {
          user,
          isVerified: user?.isVerified
        });
        setAuthState({
          isAuthenticated: !!user,
          currentUser: user,
          loading: false,
          error: null
        });
      } catch (error) {
        console.error('AuthProvider - getCurrentUser error:', error);
        setAuthState({
          isAuthenticated: false,
          currentUser: null,
          loading: false,
          error: null
        });
      }
    };
    
    initAuth();
  }, []);

  // Handle redirects for unverified users
  useEffect(() => {
    if (
      authState.isAuthenticated &&
      authState.currentUser &&
      !authState.currentUser.isVerified &&
      location.pathname !== '/verification-required' &&
      !redirectRef.current
    ) {
      console.log('AuthProvider - User not verified, redirecting to verification page', {
        email: authState.currentUser.email,
        from: location.pathname
      });
      redirectRef.current = true;
      navigate('/verification-required', {
        replace: true,
        state: { email: authState.currentUser.email, from: location }
      });
    }
    // Reset redirectRef when reaching verification-required
    if (location.pathname === '/verification-required') {
      redirectRef.current = false;
    }
  }, [authState.isAuthenticated, authState.currentUser, location.pathname, navigate, location]);

  const login = async (email: string, password: string): Promise<void> => {
    try {
      console.log('AuthProvider - Attempting login:', { email });
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
      
      const data = await AuthService.login({ email, password });
      
      console.log('AuthProvider - login success:', data);
      setAuthState({
        isAuthenticated: true,
        currentUser: data.user,
        loading: false,
        error: null
      });
    } catch (error: any) {
      console.error('AuthProvider - login error:', error);
      
      // Check if this is a verification error
      if (error.response?.status === 401 && 
          error.response?.data?.message === "Please verify your email before logging in") {
        
        // Let the Login component handle this error
        throw error;
      }
      
      // Handle other errors
      const errorMessage = error.response?.data?.message || 'Failed to login';
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }));
      
      throw error;
    }
  };

  const register = async (
    firstName: string, 
    lastName: string, 
    email: string, 
    password: string,
    planType: string = 'basic',
    planDuration: string = 'monthly',
    currency: string = 'NGN'
  ): Promise<{
    user: User;
    accessToken: string;
    payment?: {
      reference: string;
      authorizationUrl: string;
    }
  }> => {
    try {
      console.log('AuthProvider - Attempting register:', { email, planType, planDuration });
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
      
      // Update the service call to include plan info
      const data = await AuthService.register({ 
        firstName, 
        lastName, 
        email, 
        password,
        planType,
        planDuration
      });
      
      console.log('AuthProvider - register success:', {
        data,
        isVerified: data.user?.isVerified,
        planType: data.user?.premiumPlan,
        hasPayment: !!data.payment
      });
      
      // Only set verification state if we're not redirecting to payment
      if (!data.payment?.authorizationUrl) {
        localStorage.setItem('pendingVerificationEmail', email);
        navigate('/verification-required', {
          replace: true,
          state: { email }
        });
        
        setAuthState({
          isAuthenticated: false,
          currentUser: { ...data.user, email },
          loading: false,
          error: null
        });
        
        toast.success('Registration successful! Please check your email to verify your account.', {
          position: 'top-right',
          autoClose: 5000
        });
      }
      
      return data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to register';
      console.error('AuthProvider - register error:', { error, email });
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }));
      throw error;
    }
  };

  const logout = (): void => {
    AuthService.logout();
    console.log('AuthProvider - logout');
    setAuthState({
      isAuthenticated: false,
      currentUser: null,
      loading: false,
      error: null
    });
    redirectRef.current = false;
    localStorage.removeItem('pendingVerificationEmail');
  };

  const refreshUser = async (): Promise<User> => {
    try {
      console.log('AuthProvider - Attempting refreshUser');
      setAuthState(prev => ({ ...prev, loading: true }));
      const user = await AuthService.getProfile();
      console.log('AuthProvider - refreshUser success:', {
        user,
        isVerified: user?.isVerified
      });
      setAuthState({
        isAuthenticated: true,
        currentUser: user,
        loading: false,
        error: null
      });
      return user;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to refresh user';
      console.error('AuthProvider - refreshUser error:', error);
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }));
      throw error;
    }
  };

  // Handler for setting auth state
  const handleSetAuthState = (newState: Partial<AuthState>) => {
    setAuthState(prev => ({ ...prev, ...newState }));
  };

  // Log auth state changes
  useEffect(() => {
    console.log('AuthProvider - Auth state changed:', {
      isAuthenticated: authState.isAuthenticated,
      isVerified: authState.currentUser?.isVerified,
      loading: authState.loading,
      error: authState.error,
      path: location.pathname
    });
  }, [authState, location.pathname]);

  const value = useMemo(
    () => ({
      ...authState,
      login,
      register,
      logout,
      refreshUser,
      setAuthState: handleSetAuthState
    }),
    [authState]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  return useContext(AuthContext);
};

export default AuthContext;